import { useId, type ReactElement } from 'react';
import { Modal } from './Modal';
import './HowItWorks.css';

/** One rule of a way to meet people: the icon that marks it, and the line that says it. */
export type HowLine = [ReactElement, string];

/** A way and its rules, as the ? beside its name hands them to the sheet. `state` — one | rooms | groups. */
export type How = { way: string; lines: HowLine[]; state: string };

/**
 * How each way to meet people works (2026-11-04 founder: Meet people 第一页三个都要有个 how it works 问号 —— 清晰明了地讲清楚
 * 规则和玩法). The lines below are the one place each way's rules are written: the Meet people hub opens them in a sheet
 * from the ? on each of the three cards, and a group's own page shows the Groups ones in its How it works tab.
 */
export function HowList({ lines }: { lines: HowLine[] }): ReactElement {
  return (
    <ul className="hiw-list">
      {lines.map(([icon, text]) => (
        <li key={text}><span className="hiw-icon">{icon}</span><span>{text}</span></li>
      ))}
    </ul>
  );
}

/**
 * The ? beside a way's name, wherever that way is named: the same ember mark as the page title's, opening that way's
 * rules. `small` — the 18 on a section's label, where the label is the way's heading; 22 beside a name or a title.
 */
export function HowMark({ how, onHow, small = false }: { how: How; onHow: (how: How) => void; small?: boolean }): ReactElement {
  return (
    <button
      type="button"
      className={`nf-how${small ? ' is-small' : ''}`}
      aria-label={`How ${how.way} works`}
      data-log="friends_how"
      data-log-state={how.state}
      onClick={() => onHow(how)}
    >
      <span aria-hidden>?</span>
    </button>
  );
}

/** The sheet a ? opens: which way it is, its rules, and the way out. */
export function HowSheet({ how, onClose }: { how: How; onClose: () => void }): ReactElement {
  const titleId = useId();
  return (
    <Modal sheet="phone" onClose={onClose} labelledBy={titleId} className="hiw-sheet">
      <div className="hiw-inner" data-log-seen="friends_how_sheet" data-log-state={how.state}>
        <div className="hiw-head">
          <div>
            <p className="hiw-way">{how.way}</p>
            <h2 id={titleId} className="hiw-title">How it works</h2>
          </div>
          <button type="button" className="hiw-x" aria-label="Close" data-log="door_close" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <HowList lines={how.lines} />
        <button type="button" className="ember-btn ember-btn-primary hiw-got" data-log="door_close" onClick={onClose}>Got it</button>
      </div>
    </Modal>
  );
}

/* The marks beside the lines: one stroke weight, 16px on a 24 grid, decorative throughout. */

const PEOPLE = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="8" cy="8" r="3" />
    <circle cx="17" cy="11" r="2.4" />
    <path d="M3 19c.8-3 2.8-4.6 5-4.6s4.2 1.6 5 4.6M14 19c.6-2.2 1.9-3.4 3.4-3.4" />
  </svg>
);
const CARD = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <circle cx="9" cy="11" r="2.2" />
    <path d="M5.8 16.4c.6-1.6 1.8-2.4 3.2-2.4s2.6.8 3.2 2.4M15 10h3.5M15 14h3.5" />
  </svg>
);
const LOOK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16 16l4.5 4.5" />
  </svg>
);
const CHECK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12.3l2.6 2.7L16 9.5" />
  </svg>
);
const CLOCK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const MASK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c1-3.6 3.8-5.4 7-5.4s6 1.8 7 5.4" />
  </svg>
);
const STACK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4" y="4" width="16" height="5" rx="1.6" />
    <rect x="4" y="12" width="16" height="5" rx="1.6" />
    <path d="M6.5 20h11" />
  </svg>
);
const LEAVE = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" />
    <path d="M17 8.5L20.5 12 17 15.5M10 12h10" />
  </svg>
);
const PLUS = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v8M8 12h8" />
  </svg>
);
const QUESTION = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 0 1 4.8 1c0 1.7-2.3 2-2.3 3.5" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" />
  </svg>
);
const SHIELD = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const FLAME = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path d="M12 3c2 3 5 5.2 5 9.2A5 5 0 0 1 7 12.2C7 8.6 10 6.6 12 3z" fill="currentColor" />
  </svg>
);

/** One person (friends.js A FRIEND): the card, the look, the introduction, the chat and its week. */
export const HOW_ONE: HowLine[] = [
  [CARD, 'You make a card first: a name, a picture, and what you could talk about for hours.'],
  [LOOK, 'Ember looks for someone you’d click with. There’s no list to browse, and it can take a while.'],
  [CHECK, 'You each see the other’s card and why Ember thought of you two. Both say yes, and the chat opens.'],
  [CLOCK, 'A no, or three days with no answer, sends you both back to looking — no reason given either way.'],
  [MASK, 'They see your card and what you write, never your conversations with Ember.'],
  [STACK, 'One at a time. Once your chat opens, you can look for someone else.'],
  [LEAVE, 'Stop looking whenever, and nobody is told. A week into a chat, Ember asks you alone how it’s going.'],
];

/** Topic rooms (friends.js ROOMS BY TOPIC; TOPIC_MIN_MEMBERS 3, TOPIC_MAX_MEMBERS 8, SEATS_PER_PERSON 3). */
export const HOW_ROOMS: HowLine[] = [
  [PLUS, 'Put up a topic in a few words, or ask into one someone else put up. Ember checks the fit.'],
  [PEOPLE, 'A room opens once three people have picked the same thing, and takes up to eight.'],
  [FLAME, 'Ember is in the room with you. It keeps things going and looks out for everyone.'],
  [MASK, 'You go by a made-up name and picture, never your conversations with Ember.'],
  [STACK, 'Three at a time: the topics you have waiting plus the rooms you’re in.'],
  [LEAVE, 'Leave a room or stop a topic whenever, and nobody is told.'],
];

/** Groups (peer-groups.js). `name` — their room name, where the surface knows it (a group's own page). */
export const howGroups = (name?: string | null): HowLine[] => [
  [PEOPLE, 'A private group for people living through the same thing. Groups open up from what you’ve told Ember.'],
  [QUESTION, 'Post a question, or share what’s going on, whenever you need.'],
  [CLOCK, 'Others answer when they stop by. There’s no live chat, so there’s no rush.'],
  [MASK, name ? `Everyone here goes by a made-up name. You’re ${name}.` : 'Everyone here goes by a made-up name, not your own.'],
  [SHIELD, 'Every post and answer gets a quick read first, to keep the group kind and safe.'],
  [FLAME, 'Ember is here too. It answers, and sometimes starts a quick poll or a chain.'],
];
